import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { useClientProfile } from './ClientProfileContext';
import { notifyProductAdded, notifyProductEdited } from '../../utils/notifyClientPortal';

const VendrProductsContext = createContext();

export const useVendrProducts = () => {
  const context = useContext(VendrProductsContext);
  if (!context) {
    throw new Error('useVendrProducts must be used within a VendrProductsProvider (and inside ClientProfileProvider)');
  }
  return context;
};

export const VendrProductsProvider = ({ children }) => {
  const { profile } = useClientProfile();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProducts = useCallback(async () => {
    if (!profile?.id) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('client_products')
        .select('*')
        .eq('client_profile_id', profile.id)
        .order('updated_at', { ascending: false });

      if (fetchError) {
        setError(fetchError.message);
        setProducts([]);
        return;
      }
      setProducts(data ?? []);
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const createProduct = async (productData) => {
    if (!profile?.id) return { success: false, error: 'Complete company setup first' };
    try {
      const { data, error: insertError } = await supabase
        .from('client_products')
        .insert([{ ...productData, client_profile_id: profile.id }])
        .select()
        .single();
      if (insertError) return { success: false, error: insertError.message };
      setProducts((prev) => [data, ...prev]);
      notifyProductAdded(data, profile);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const toImportPayload = (row) => ({
    sku_number: row.sku_number,
    product_name: row.product_name,
    product_description: row.product_description || null,
    volume: row.volume != null ? String(row.volume) : null,
    alcohol_abv: row.alcohol_abv || null,
    commodity_code: row.commodity_code,
    country_of_manufacture: row.country_of_manufacture,
    shelf_life: row.shelf_life || null,
    item_type: row.item_type || null,
    pack_size: null,
    quantity_per_case: row.quantity_per_case,
    bottle_material: row.bottle_material || null,
    can_material: null,
    cases_per_pallet: row.cases_per_pallet,
    net_case_weight: row.net_case_weight,
    gross_case_weight: row.gross_case_weight,
    case_height_mm: row.case_height_mm,
    case_length_mm: row.case_length_mm,
    case_width_mm: row.case_width_mm,
    rrp_per_bottle_gbp: row.rrp_per_bottle_gbp,
    rrp_per_case_b2b_gbp: row.rrp_per_case_b2b_gbp,
    cost_price_per_bottle_gbp: row.cost_price_per_bottle_gbp,
    cost_price_per_case_gbp: row.cost_price_per_case_gbp,
    currency_rrp_bottle: row.currency_rrp_bottle || 'GBP',
    currency_rrp_case: row.currency_rrp_case || 'GBP',
    currency_cost_bottle: row.currency_cost_bottle || 'GBP',
    currency_cost_case: row.currency_cost_case || 'GBP',
    status: 'draft',
  });

  const bulkImportProducts = async (rows = []) => {
    if (!profile?.id) return { success: false, error: 'Complete company setup first' };
    if (!Array.isArray(rows) || rows.length === 0) {
      return { success: false, error: 'No valid rows to import' };
    }

    try {
      const { data: existingProducts, error: existingError } = await supabase
        .from('client_products')
        .select('id, sku_number')
        .eq('client_profile_id', profile.id);

      if (existingError) return { success: false, error: existingError.message };

      const skuToId = new Map(
        (existingProducts || [])
          .filter((entry) => entry?.sku_number)
          .map((entry) => [String(entry.sku_number).trim().toLowerCase(), entry.id]),
      );

      const failedRows = [];
      let insertedCount = 0;
      let updatedCount = 0;

      for (const row of rows) {
        const rowNumber = row.__rowNumber || 0;
        const payload = toImportPayload(row);
        const skuKey = String(row.sku_number || '').trim().toLowerCase();
        const existingId = skuToId.get(skuKey);

        if (existingId) {
          const { data, error: updateError } = await supabase
            .from('client_products')
            .update({ ...payload, updated_at: new Date().toISOString() })
            .eq('id', existingId)
            .eq('client_profile_id', profile.id)
            .select('id')
            .single();

          if (updateError || !data?.id) {
            failedRows.push({
              row: rowNumber,
              field: 'sku_number',
              error: updateError?.message || 'Failed to update product',
            });
          } else {
            updatedCount += 1;
          }
          continue;
        }

        const { data, error: insertError } = await supabase
          .from('client_products')
          .insert([{ ...payload, client_profile_id: profile.id }])
          .select('id')
          .single();

        if (insertError || !data?.id) {
          failedRows.push({
            row: rowNumber,
            field: 'sku_number',
            error: insertError?.message || 'Failed to create product',
          });
          continue;
        }

        skuToId.set(skuKey, data.id);
        insertedCount += 1;
      }

      await loadProducts();
      return {
        success: true,
        data: {
          insertedCount,
          updatedCount,
          failedRows,
        },
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      const { data, error: updateError } = await supabase
        .from('client_products')
        .update({ ...productData, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('client_profile_id', profile.id)
        .select()
        .single();
      if (updateError) return { success: false, error: updateError.message };
      setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
      notifyProductEdited(data, profile);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteProduct = async (id) => {
    try {
      const { error: deleteError } = await supabase
        .from('client_products')
        .delete()
        .eq('id', id)
        .eq('client_profile_id', profile.id);
      if (deleteError) return { success: false, error: deleteError.message };
      setProducts((prev) => prev.filter((p) => p.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const getProductById = (id) => products.find((p) => p.id === id);

  const value = {
    products,
    loading,
    error,
    loadProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    bulkImportProducts,
  };

  return (
    <VendrProductsContext.Provider value={value}>
      {children}
    </VendrProductsContext.Provider>
  );
};
